import { ApiClient } from "../clients/api_client";
import { Canvas } from "../clients/types";

export class CanvasCache {
    private static instance: CanvasCache|null = null;
    private activeCanvas: Canvas|null|undefined = undefined;
    private pendingCanvas: Canvas|null|undefined = undefined;
    private canvases: Record<number, Canvas> = {};

    private constructor() {}

    public static getInstance(): CanvasCache {
        if (CanvasCache.instance === null) {
            CanvasCache.instance = new CanvasCache();
            CanvasCache.instance.init();
        }

        return CanvasCache.instance;
    }

    public refresh(): CanvasCache {
        this.activeCanvas = undefined;
        this.pendingCanvas = undefined;
        return this;
    }

    public async getCanvas(canvasId: number): Promise<Canvas|null> {
        if (canvasId in this.canvases) {
            return this.canvases[canvasId];
        }

        const canvas = await ApiClient.getInstance().queryCanvas(canvasId);

        if (canvas === null) {
            return null;
        }

        this.canvases[canvasId] = canvas;
        return canvas;
    }

    public async getActiveCanvas(): Promise<Canvas|null> {
        if (this.activeCanvas !== undefined) {
            return this.activeCanvas;
        }

        const active = await ApiClient.getInstance().queryActiveCanvases();

        let minCanvas: Canvas|null = null;

        for (const canvasId of active) {
            const canvasProps = await ApiClient.getInstance().queryCanvas(canvasId);

            if (!canvasProps) {
                continue;
            }

            // Searching for earliest ending active canvas
            if (minCanvas === null || canvasProps.blockRange.end < minCanvas.blockRange.end) {
                minCanvas = canvasProps;
            }
        }

        this.activeCanvas = minCanvas;
        return this.activeCanvas;
    }

    public async getPendingCanvas(): Promise<Canvas|null> {
        if (this.pendingCanvas !== undefined) {
            return this.pendingCanvas;
        }

        const pending = await ApiClient.getInstance().queryPendingCanvases();

        let minCanvas: Canvas|null = null;

        for (const canvasId of pending) {
            const canvasProps = await ApiClient.getInstance().queryCanvas(canvasId);

            if (!canvasProps) {
                continue;
            }

            // Searching for earliest starting pending canvas
            if (minCanvas === null || canvasProps.blockRange.start < minCanvas.blockRange.start) {
                minCanvas = canvasProps;
            }
        }

        this.pendingCanvas = minCanvas;
        return this.pendingCanvas;
    }

    private init(): void {
        ApiClient.getInstance().subscribeOnClose(() => this.init());
        ApiClient.getInstance().subscribeCanvasCreated((canvasId) => this.reset(canvasId)).catch((reason) => console.error(reason));
        ApiClient.getInstance().subscribeCanvasModified((canvasId) => this.reset(canvasId)).catch((reason) => console.error(reason));
        ApiClient.getInstance().subscribeCanvasRemoved((canvasId) => this.reset(canvasId)).catch((reason) => console.error(reason));
        ApiClient.getInstance().subscribeCanvasCompleted((canvasId) => this.reset(canvasId)).catch((reason) => console.error(reason));
    }

    private reset(canvasId: number): void {
        this.activeCanvas = undefined;
        this.pendingCanvas = undefined;
        delete this.canvases[canvasId];
    }
}
